import each from 'lodash/each';
import find from 'lodash/find';
import get from 'lodash/get';
import invert from 'lodash/invert';
import isEmpty from 'lodash/isEmpty';
import isUndefined from 'lodash/isUndefined';
import replace from 'lodash/replace';

import { composeQuery, orderBy, select } from 'common/soql_builder';
import { OrderByDsl } from 'common/soql_builder/orderBy';
import { NamedSelect } from 'common/soql_builder/select';
import { ViewColumn } from 'common/types/viewColumn';

import { getSoqlMetaBundle } from '../dataProviders/getSoqlMetaBundle';
import { getDisplayableColumns } from '../dataProviders/MetadataProvider';
import { CustomSortConfig, Hierarchy, OrderConfig, Vif } from '../vif';
import { GROUP_COLUMN_PREFIX } from '../views/agGridReact/Constants';

import { selectAlias } from './SoqlAliasHelpers';
import { mapSoqlRowsResponseToTable, whereClauseFilteringOwnColumn } from './SoqlHelpers';
import { buildCasesParts } from './TableDataHelpers';
import { unaliasRowData } from './TableDataQueryHelpers';
import { getDefaultSortOrder } from './TableDataSortHelpers';
import { getDatasetUid, isCustomSortEnabled } from './VifSelectors';

export function createExportSelectAliasMap(columns: ViewColumn[]) {
  return columns
    .map((col, idx) => [col.fieldName, selectAlias(idx)])
    .reduce((aliases, [colId, alias]) => ({ ...aliases, [colId]: alias }), {});
}

/**
 *
 * @param {ViewColumn []} cols - All of the columns being used in the table visualization.
 * @param {OrderConfig []} order - Table's sort configuration
 * @param {number} pageSize - Number of rows to be fetched
 * @param {number} startIndex - Starting index to fetch from the dataset
 * @param {string} legacyWhereClause - The output of SoqlHelpers.whereClauseFilteringOwnColumn
 * @returns - a query to be used for table exports in the Soql Query Builder, invokeSoql function.
 */
export function tableExportDataQuery(
  cols: ViewColumn[],
  order: OrderConfig[],
  pageSize: number,
  startIndex: number,
  columnSortConfig: CustomSortConfig | undefined,
  legacyWhereClause?: string,
) {
  const selectAliasMap: { [key: string]: string } = createExportSelectAliasMap(cols);
  const selectQueries: NamedSelect[] = [];
  each(cols, (col) => {
    selectQueries.push(select(col, selectAliasMap[col.fieldName]));
  });
  let orderQueries: (OrderByDsl | undefined)[] = [];
  orderQueries = order
    .map((orderConfig: OrderConfig) => {
      const col = find(
        cols,
        (c) => c.fieldName === replace(orderConfig.columnName, `${GROUP_COLUMN_PREFIX}-`, ''),
      );
      const ordering = orderConfig.ascending === true ? 'ascending' : 'descending';

      if (isUndefined(col) || col.fieldName === ':id') {
        return;
      }
      return orderBy(col, ordering);
    })
    .filter((orderForSoql) => !isUndefined(orderForSoql));

  return composeQuery({
    selects: selectQueries,
    limit: pageSize,
    offset: startIndex,
    legacyWhereClause,
    orders: orderQueries,
    ...(isCustomSortEnabled() &&
      !isUndefined(columnSortConfig) && {
        cases: buildCasesParts(selectQueries, columnSortConfig, order),
      }),
  });
}

export const AG_SOQL_EXPORT_QUERY_LIMIT = 100000;

/**
 * Utilizes the Soql Query Builder or SoqlDataProvider to fetch rows for exporting AgGrid tables
 */
export async function setAgSoqlExportQuery(
  vif: Vif,
  order: OrderConfig[],
  previousOrder: OrderConfig[],
  selectedFiltered: boolean,
) {
  const { soqlDataProvider, metadataProvider } = getSoqlMetaBundle(vif);
  const datasetMetadata = await metadataProvider.getDatasetMetadata();
  const displayableColumns = getDisplayableColumns(datasetMetadata);
  const pageSize = AG_SOQL_EXPORT_QUERY_LIMIT;
  const startIndex = 0;
  if (isUndefined(order) || isEmpty(order)) {
    const defaultSortOrder =
      (await getDefaultSortOrder(datasetMetadata, getDatasetUid(vif), previousOrder)) || [];

    order = defaultSortOrder;
  }
  const hierarchies = get(vif, 'series[0].dataSource.hierarchies', []);
  const activeHierarchyId = get(vif, 'series[0].dataSource.activeHierarchyId');
  const activeHierarchyIndex = hierarchies.findIndex((h: Hierarchy) => h.id == activeHierarchyId);
  let columnSortConfig = undefined;

  if (activeHierarchyIndex !== -1 && isCustomSortEnabled()) {
    columnSortConfig = get(
      vif,
      `series[0].dataSource.hierarchies[${activeHierarchyIndex}.columnSortConfig`,
      [],
    );
  }

  let legacyWhereClause;
  if (selectedFiltered) {
    legacyWhereClause = whereClauseFilteringOwnColumn(vif, 0);
  }

  const soqlQueryParams = [
    displayableColumns,
    order,
    pageSize,
    startIndex,
    columnSortConfig,
    legacyWhereClause,
  ];
  const tableData = await soqlDataProvider.invokeSoqlQuery(tableExportDataQuery, soqlQueryParams);
  const selectAliasMap = createExportSelectAliasMap(displayableColumns);
  const aliasMap = { ...invert(selectAliasMap) };

  const unaliasedValues = unaliasRowData(tableData, aliasMap);
  const mappedTableData = mapSoqlRowsResponseToTable(displayableColumns, unaliasedValues, 'agTable');
  return mappedTableData;
}
